import { ROUTES } from '../../utils/constants';
import type { SchoolStudentFeatureKey } from '../platform/types/featureAccess';
import type { ResolvedLearnerCurriculum } from '../curriculum/learnerCurriculumTypes';
import {
  isEffectiveMalayLearner,
  isEffectiveTamilLearner,
  shouldDeferMixedTenantLearnerPocNav,
} from '../curriculum/learnerCurriculumOverride';
import {
  KEELADI_DIGITAL_BOOK_ACTIVITY_ID,
} from '../digital-book/adapters/keeladiDigitalBookAdapter';
import { studentKeeladiInteractiveBookPath } from '../../utils/constants';
import { buildTamilCompositionWritingStudioUrl } from '../tutor/tamilCompositionWritingRoute';

/** Malay POC learner routes — hidden from Tamil-track learners (legacy subtractive helpers). */
export const MALAY_POC_NAV_ROUTES = [
  ROUTES.STUDENT_READING_COMPREHENSION,
  ROUTES.STUDENT_NARRATIVE_WRITING,
  ROUTES.STUDENT_NARRATIVE_WRITING_TUTOR,
  ROUTES.STUDENT_NARRATIVE_WRITING_WRITE,
] as const;

export type LearnerPocTrack = 'malay' | 'tamil' | 'neutral';

export type StudentPocNavIconKey = 'home' | 'bookOpen' | 'messageCircle' | 'pen';

export interface StudentPocNavItemDefinition {
  id: string;
  labelKey: string;
  route: string;
  iconKey: StudentPocNavIconKey;
  schoolFeatureKey?: SchoolStudentFeatureKey;
  activeRoutes?: string[];
  /** Malay Jendela is limited to grade 7 in the current POC. */
  malayReadingGradeGate?: number;
}

export function isMalayPocNavRoute(route: string): boolean {
  if (route === ROUTES.STUDENT_READING_COMPREHENSION) return true;
  if (route.startsWith(`${ROUTES.STUDENT_NARRATIVE_WRITING}`)) return true;
  if (route.startsWith('/student/malay/expository-writing')) return true;
  return false;
}

export function shouldHideMalayPocNavForTamilLearner(
  route: string,
  isEffectiveTamil: boolean,
): boolean {
  return isEffectiveTamil && isMalayPocNavRoute(route);
}

/** Direct entry to Keeladi exact-page interactive book (Tamil TB U5 L2). */
export function buildTamilKeeladiNavUrl(): string {
  return studentKeeladiInteractiveBookPath();
}

/** Direct entry to Tamil Composition Writing studio. */
export function buildTamilCompositionNavUrl(): string {
  return buildTamilCompositionWritingStudioUrl();
}

export { KEELADI_DIGITAL_BOOK_ACTIVITY_ID };

export function resolveLearnerTrackContext(input: {
  curriculum: ResolvedLearnerCurriculum | null | undefined;
  legacyTenantIsMalay: boolean;
  curriculumLoading: boolean;
}): {
  track: LearnerPocTrack;
  isLearnerMalay: boolean;
  isLearnerTamil: boolean;
  deferMixedTenantPocNav: boolean;
} {
  const deferMixedTenantPocNav = shouldDeferMixedTenantLearnerPocNav(input);
  const isLearnerTamil = isEffectiveTamilLearner({
    curriculum: input.curriculum,
    legacyTenantIsMalay: input.legacyTenantIsMalay,
  });
  const isLearnerMalay =
    !deferMixedTenantPocNav &&
    isEffectiveMalayLearner({
      curriculum: input.curriculum,
      legacyTenantIsMalay: input.legacyTenantIsMalay,
    });

  let track: LearnerPocTrack = 'neutral';
  if (isLearnerTamil) track = 'tamil';
  else if (isLearnerMalay) track = 'malay';

  return { track, isLearnerMalay, isLearnerTamil, deferMixedTenantPocNav };
}

function buildIbooksNavItem(): StudentPocNavItemDefinition {
  return {
    id: 'ibooks',
    labelKey: 'sidebar.student.ibooks',
    route: ROUTES.STUDENT_IBOOKS,
    iconKey: 'bookOpen',
    schoolFeatureKey: 'ibook_reader_enabled',
    activeRoutes: [
      ROUTES.STUDENT_IBOOKS,
      ROUTES.STUDENT_IBOOK_READER,
      ROUTES.STUDENT_INTERACTIVE_BOOK_READER,
    ],
  };
}

function buildTamilPocNavItems(): StudentPocNavItemDefinition[] {
  return [buildIbooksNavItem()];
}

function buildMalayPocNavItems(): StudentPocNavItemDefinition[] {
  return [buildIbooksNavItem()];
}

function buildNeutralPocNavItems(): StudentPocNavItemDefinition[] {
  return [
    {
      id: 'home',
      labelKey: 'sidebar.student.home',
      route: ROUTES.STUDENT_DASHBOARD,
      iconKey: 'home',
    },
    buildIbooksNavItem(),
  ];
}

/** Authoritative POC sidebar item list for the resolved learner track. */
export function buildStudentPocNavItems(input: {
  track: LearnerPocTrack;
  rosterGrade?: number | string | null;
}): StudentPocNavItemDefinition[] {
  switch (input.track) {
    case 'tamil':
      return buildTamilPocNavItems();
    case 'malay':
      return buildMalayPocNavItems();
    case 'neutral':
      return buildNeutralPocNavItems();
    default: {
      const _exhaustive: never = input.track;
      return _exhaustive;
    }
  }
}

export function filterStudentPocNavByFeatures(
  items: StudentPocNavItemDefinition[],
  isFeatureEnabled: (key: SchoolStudentFeatureKey) => boolean,
): StudentPocNavItemDefinition[] {
  return items.filter((item) => {
    if (!item.schoolFeatureKey) return true;
    return isFeatureEnabled(item.schoolFeatureKey);
  });
}

export function getStudentPocNavLabelKeys(input: {
  track: LearnerPocTrack;
  rosterGrade?: number | string | null;
}): string[] {
  return buildStudentPocNavItems(input).map((item) => item.labelKey);
}
